import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import Input, { InputProps } from './Input';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
    showRequirements?: boolean;
    password?: string;
}

interface PasswordRequirement {
    label: string;
    met: boolean;
}

/**
 * Password input component with show/hide toggle and requirements display
 * Matches the onboarding design mockups
 */
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ showRequirements = true, password = '', className = '', label, id, ...props }, ref) => {
        const { t } = useTranslation();
        const [showPassword, setShowPassword] = useState(false);

        // Calculate password requirements
        const requirements: PasswordRequirement[] = [
            {
                label: t('password_input.req_length'),
                met: password.length >= 8,
            },
            {
                label: t('password_input.req_upper'),
                met: /[A-Z]/.test(password),
            },
            {
                label: t('password_input.req_lower'),
                met: /[a-z]/.test(password),
            },
            {
                label: t('password_input.req_number'),
                met: /[0-9]/.test(password),
            },
        ];

        // Render the label OUTSIDE the relative wrapper so the show/hide button
        // is positioned relative to just the input, not (label + input).
        return (
            <div className="w-full">
                {label && (
                    <label
                        htmlFor={id}
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        {label}
                    </label>
                )}
                <div className="relative">
                    <Input
                        ref={ref}
                        type={showPassword ? 'text' : 'password'}
                        className={`pr-12 ${className}`}
                        id={id}
                        {...props}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        tabIndex={-1}
                        aria-label={showPassword ? t('password_input.hide') : t('password_input.show')}
                    >
                        {showPassword ? (
                            <EyeSlashIcon className="w-5 h-5" />
                        ) : (
                            <EyeIcon className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {showRequirements && password.length > 0 && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg text-left">
                        <p className="text-xs text-gray-600 mb-2">{t('password_input.requirements')}</p>
                        <div className="space-y-1">
                            {requirements.map((req, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    {req.met ? (
                                        <svg
                                            className="w-4 h-4 text-green-500 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-4 h-4 text-gray-300 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    )}
                                    <span className={`text-xs ${req.met ? 'text-gray-700' : 'text-gray-500'}`}>
                                        {req.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
